'use client';

import styles from './Layout.module.css';
import tinycolor from 'tinycolor2';
import { usePathname } from 'next/navigation';
import type { LayoutProps } from './Layout.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { Header } from './components/Header';
import { HeaderCollapsed } from './components/HeaderCollapsed';
import { Assistant, LeftNavAdmin, LeftNavClient } from '@/_components';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { getClasses } from '@/_utils/classnames';
import {
  type FC,
  useRef,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import { usePrevious } from '@/_hooks/usePrevious';
import { UserContext } from '@/_contexts/UserProvider';
import { UserPreviewProvider } from '@/_contexts/admin/UserPreviewProvider';
import { CopyTextDialog } from './components/CopyTextDialog';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

export const Layout: FC<LayoutProps> = ({ children }) => {
  const asideRef = useRef<HTMLElement>(null);
  const [assistantSize, setAssistantSize] = useState(30);

  const {
    isPublic,
    isStandalone,
    isCentered,
    filterContent,
    setFilterContent,
    isAssistantOpen,
  } = useContext(LayoutContext);
  const { isAdvisor, isLoggedIn } = useContext(UserContext);
  const pathname = usePathname();

  const previousContent = usePrevious(filterContent);

  const layoutClasses: string = getClasses(styles.layout, {
    [styles.layoutNormal]: !isPublic,
    [styles.layoutCentered]: isCentered,
    [styles.layoutPublic]: isPublic,
    [styles.layoutStandalone]: isStandalone,
    [styles.layoutWithAssistant]: isAssistantOpen,
  });

  const { colorPrimary } = useContext(FirmSettingsContext);

  const light = tinycolor(colorPrimary).desaturate(50).lighten(60);
  const lighter =
    light.toHex() === 'ffffff'
      ? tinycolor(colorPrimary).desaturate(10).lighten(35)
      : light;

  const customColorSchemeObject: object =
    colorPrimary && (!isAdvisor || !isLoggedIn)
      ? {
          '--bg-accent': colorPrimary,
          '--bg-accent-active': tinycolor(colorPrimary).darken(15),
          '--bg-accent-disabled': tinycolor(colorPrimary)
            .lighten(20)
            .desaturate(25),
          '--bg-accent-ghost': tinycolor(colorPrimary).lighten(75),
          '--bg-accent-ghost-active': tinycolor(colorPrimary).lighten(50),
          '--bg-accent-ghost-hover': tinycolor(colorPrimary).lighten(60),
          '--bg-accent-hover': tinycolor(colorPrimary).darken(5),
          '--bg-accent-subtle': lighter,
          '--bg-accent-subtle-active': tinycolor(colorPrimary).lighten(85),
          '--bg-accent-subtle-hover': tinycolor(colorPrimary).lighten(75),
          '--border-accent': colorPrimary,
          '--border-accent-disabled': tinycolor(colorPrimary).lighten(20),
          '--text-accent': colorPrimary,
          '--text-accent-disabled': tinycolor(colorPrimary).lighten(20),
        }
      : {};

  const onClose = () => setFilterContent(null);

  const layoutAsideClasses: string = getClasses(
    styles.layout__filters,
    'elevation-md',
    {
      [styles.layout__filtersIn]: !!filterContent,
      [styles.layout__filtersOut]: !filterContent && !!previousContent,
    },
  );

  const shouldShowClientPreview = pathname.startsWith('/preview/user');

  // TODO: remove custom logic for template preview pages once `/template` page is migrated to React
  const isTemplatePreview = pathname.startsWith('/preview/template');

  const handlePanelResize = useCallback((sizes: number[]) => {
    if (sizes.length === 2) {
      const newAssistantSize = sizes[1];

      setAssistantSize(newAssistantSize);
      document.cookie = `assistantPanelSize=${newAssistantSize}; path=/; max-age=${60 * 60 * 24 * 365}`;
    }
  }, []);

  // Calculate minimum size percentage for 360px width
  const minAssistantSizePercent =
    typeof window === 'undefined'
      ? 30
      : (360 / (window?.innerWidth ?? 0)) * 100;

  useEffect(() => {
    const savedSize = document.cookie
      .split('; ')
      .find((row) => row.startsWith('assistantPanelSize='))
      ?.split('=')?.[1];

    if (savedSize) {
      setAssistantSize(Number(savedSize));
    }
  }, []);

  return (
    <>
      <PanelGroup
        direction="horizontal"
        style={{ height: '100vh' }}
        onLayout={handlePanelResize}
        suppressHydrationWarning={true}
      >
        <Panel
          defaultSize={isAssistantOpen ? 100 - assistantSize : 100}
          minSize={30}
        >
          <main
            data-testid="Layout"
            className={layoutClasses}
            style={customColorSchemeObject}
          >
            {isPublic && !isStandalone && <HeaderCollapsed />}
            {!isPublic && !isCentered && <Header />}

            {!isPublic && !isCentered && !isTemplatePreview && isLoggedIn && (
              <>
                {isAdvisor ? (
                  shouldShowClientPreview ? (
                    <UserPreviewProvider>
                      <LeftNavClient isPreview />
                    </UserPreviewProvider>
                  ) : (
                    <LeftNavAdmin />
                  )
                ) : (
                  <LeftNavClient />
                )}
              </>
            )}
            <div className={styles.layout__content}>{children}</div>

            <aside ref={asideRef} className={layoutAsideClasses}>
              {filterContent}
            </aside>
            {!!filterContent && (
              <div className={styles.filterOverlay} onClick={onClose} />
            )}
          </main>
        </Panel>

        <PanelResizeHandle
          style={
            isAssistantOpen
              ? {
                  width: '1px',
                  background: 'transparent',
                  borderRight: '1px solid var(--border-primary)',
                }
              : { display: 'none' }
          }
        />
        <Panel
          defaultSize={assistantSize}
          minSize={Math.max(30, minAssistantSizePercent)}
          maxSize={70}
          style={isAssistantOpen ? { overflow: 'hidden' } : { display: 'none' }}
        >
          <Assistant />
        </Panel>
      </PanelGroup>
      <CopyTextDialog />
    </>
  );
};
